import {
  Grid
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { getObjetivos } from "../../controllers/operaciones";
import { useProyecto } from "../../data/ProyectoContext";
import { useAuthContext } from "../../hooks/Auth";
import { UseSetLenguajePorPais } from "../../hooks/UseSetLenguajePorPais";
import {
  iObjetivoAprendizaje,
  SelectedIndicator
} from "../../interfaces/ObjetivosAprendizaje/iObjetivosAprendizaje";
import { useActividadesContext } from "../UmmIACategories/Hook/useActividades";
import SingleType from "./SingleType";

interface Props {
  nombreAsignatura: any;
  nombreNivel: any;
}

const GetObjetivosAprendizaje: FC<Props> = ({
  nombreAsignatura,
  nombreNivel,
}) => {
  const [dataOa, setDataOa] = useState<iObjetivoAprendizaje[]>([]);
  const [selectedIndicators, setSelectedIndicators] =
    useState<SelectedIndicator>({});
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null);
  const { user } = useAuthContext();
  const {
    setPromptData,
    setObjetivosSeleccionados,
    setIsOAAdded,
  } = useActividadesContext();

  UseSetLenguajePorPais();

  const { proyecto, setProyecto } = useProyecto();

  // Consulta de la API con asignatura y nivel
  useEffect(() => {
    if (!nombreAsignatura || !nombreNivel) return;

    async function fetchObjectives() {
      if (user?.pais) {
        try {
          const res = await getObjetivos(
            user.pais,
            nombreAsignatura,
            nombreNivel
          );
          console.log("Respuesta de objetivos:", res);
          if (res.status === 200) {
            setDataOa(res.data.body);
          }
        } catch (error) {
          console.log(error);
        }
      }
    }

    fetchObjectives();
  }, [nombreAsignatura, nombreNivel, user]);

  // Sincronizar selectedIndicators con los indicadores persistidos en el proyecto
  useEffect(() => {
    if (!proyecto.objetivos_aprendizaje) return;

    const formattedObj = proyecto.objetivos_aprendizaje.map((oa) => [
      oa.id_objetivo,
      oa.indicadores,
    ]);
    // Transformamos proyecto.objetivos_aprendizaje a {string: string[]}, donde el key es el objetivo y
    // value son los indicadores
    const persistedIndicators: SelectedIndicator =
      Object.fromEntries(formattedObj);
    setSelectedIndicators(persistedIndicators);
  }, [proyecto]);

  // Manejo de selección de un indicador individual
  const handleIndicatorSelect = (idObjetivo: string, indicador: string) => {
    setSelectedIndicators((prev) => {
      const currentSelection = prev[idObjetivo] || [];
      if (currentSelection.includes(indicador)) {
        return {
          ...prev,
          [idObjetivo]: currentSelection.filter((item) => item !== indicador),
        };
      }
      return {
        ...prev,
        [idObjetivo]: [...currentSelection, indicador],
      };
    });
  };

  // Función para seleccionar o deseleccionar todos los indicadores de un objetivo
  const handleToggleAllIndicators = (
    idObjetivo: string,
    allIndicators: string[]
  ) => {
    const allSelected =
      selectedIndicators[idObjetivo] &&
      selectedIndicators[idObjetivo].length === allIndicators.length;
    setSelectedIndicators((prev) => ({
      ...prev,
      [idObjetivo]: allSelected ? [] : allIndicators,
    }));
  };

  // Agregar un objetivo al proyecto usando id_objetivo como identificador
  const handleAddObjectiveToProject = (objetivo: iObjetivoAprendizaje) => {
    const idObjetivo = objetivo.id_objetivo;
    const selectedObjectiveIndicators = selectedIndicators[idObjetivo] || [];
    setProyecto((prevProyecto) => {
      const objetivoExistente = prevProyecto.objetivos_aprendizaje.find(
        (oa) => oa.id_objetivo === idObjetivo
      );
      if (!objetivoExistente) {
        return {
          ...prevProyecto,
          objetivos_aprendizaje: [
            ...prevProyecto.objetivos_aprendizaje,
            {
              id_objetivo: idObjetivo,
              codigo: objetivo.codigo,
              objetivo: objetivo.objetivo,
              indicadores: selectedObjectiveIndicators,
              tipo: objetivo.tipo,
              materia: objetivo.asignatura,
              grado: objetivo.nivel,
            },
          ],
        };
      }
      return prevProyecto;
    });

    // Actualizar datos del prompt
    setPromptData((prevData: any) => ({
      ...prevData,
      objetivos: [
        ...(prevData.objetivos || []),
        {
          objetivo: objetivo.objetivo,
          tipo: objetivo.tipo,
          indicadores: selectedObjectiveIndicators,
        },
      ],
    }));

    // Marcar el objetivo como seleccionado
    setObjetivosSeleccionados((prev) => ({
      ...prev,
      [idObjetivo]: true,
    }));

    setIsOAAdded(true);
  };

  // Eliminar objetivo del proyecto utilizando id_objetivo
  const handleRemoveObjectiveFromProject = (objetivo: iObjetivoAprendizaje) => {
    const idObjetivo = objetivo.id_objetivo;
    setProyecto((prevProyecto) => {
      const updatedObjetivos = prevProyecto.objetivos_aprendizaje.filter(
        (oa) => oa.id_objetivo !== idObjetivo
      );
      if (updatedObjetivos.length === 0) {
        setIsOAAdded(false);
      }
      return {
        ...prevProyecto,
        objetivos_aprendizaje: updatedObjetivos,
      };
    });

    // Actualizar objetivos seleccionados
    setObjetivosSeleccionados((prevSeleccionados) => {
      const updatedSeleccionados = { ...prevSeleccionados };
      delete updatedSeleccionados[idObjetivo];
      return updatedSeleccionados;
    });

    // Eliminar los indicadores relacionados
    setSelectedIndicators((prev) => {
      const updatedIndicators = { ...prev };
      delete updatedIndicators[idObjetivo];
      return updatedIndicators;
    });

    // Actualizar promptData para reflejar la eliminación del objetivo
    setPromptData((prevData: any) => {
      const updatedObjetivos = (prevData.objetivos || []).filter(
        (o: any) => o.objetivo !== objetivo.objetivo
      );
      return {
        ...prevData,
        objetivos: updatedObjetivos,
      };
    });
  };

  // Definir el orden de los tipos: primero Basal y luego Complementarios
  const orderedTypes = [...new Set(dataOa.map((item) => item.tipo))].sort();

  return (
    <Grid container spacing={4} m={4} mt={-5} maxWidth={"90%"}>
      {orderedTypes.map((category) => (
        <SingleType
          key={category}
          handleIndicatorSelect={handleIndicatorSelect}
          selectedIndicators={selectedIndicators}
          handleAddObjectiveToProject={handleAddObjectiveToProject}
          handleToggleAllIndicators={handleToggleAllIndicators}
          handleRemoveObjectiveFromProject={handleRemoveObjectiveFromProject}
          expandedPanel={expandedPanel}
          setExpandedPanel={setExpandedPanel}
          category={category}
          dataOa={dataOa}
          setSelectedIndicators={setSelectedIndicators}
        />
      ))}
    </Grid>
  );
};

export default GetObjetivosAprendizaje;
